import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ObjectTypeFieldDataType } from '../../../../../common/src/domain/settings/objectType/ObjectTypeFieldDataType';
import { Roles } from '../../../../../common/src/enums/Roles';
import { getRequestBodySchema } from '../../../../../common/src/routes/openApiSchemaHelpers';
import { useTranslate } from '../../../mixins/translate/translate';
import { useOpenApiFetch } from '../../../store/useOpenApiFetch';
import { useCurrentAppContext } from '../../../utils/useCurrentAppContext';
import {
    Content,
    EditPanel,
    EditPanelButtons,
    EditPanelCell,
    EditPanelRow,
    Panel,
    PanelBody,
    PanelButton,
    Topbar,
    TopbarCrumbActive,
    TopbarLeft,
    Tray,
} from '../../elements';
import { useUserHasRole } from '../../utils/authorization/useUserHasRole';
import { ValidatingFormInputTags } from '../../validatingComponents/ValidatingFormInputTags';
import { ValidatingJsonSchemaForm } from '../../validatingComponents/ValidatingJsonSchemaForm';
import {
    ValidatingCulturedFormInputNumber,
    ValidatingEditPanelSaveButton,
    ValidatingFilterDropdown,
    ValidatingFilterDropdownWithUrl,
    ValidatingFormDependentSection,
    ValidatingFormEnumValue,
    ValidatingFormInputText,
} from '../../validatingElements';

export function ObjectTypeFieldAddEdit(props: { params: { objectTypeFieldId?: string; objectTypeId: string } }) {
    const { objectTypeId, objectTypeFieldId } = props.params;
    const isInEditMode = objectTypeFieldId !== undefined;
    const translate = useTranslate();
    const { userHasRole } = useUserHasRole();
    const isAdmin = userHasRole([Roles.Administrator]);
    const currentAppContext = useCurrentAppContext();
    const [clientCompanyState, setClientCompanyState] = useState<{ defaultId: string | undefined; showDropdown: boolean } | undefined>();
    const navigate = useNavigate();

    const { data } = useOpenApiFetch('/api/objectTypes/{objectTypeId}', 'get', { objectTypeId }, []);

    // eslint-disable-next-line @typescript-eslint/require-await
    async function handleSavedOrCancelled() {
        navigate(`/settings/objecttypes/${props.params.objectTypeId}/objectTypeFields`);
    }

    useEffect(() => {
        if (data === undefined) {
            return;
        }
        if (data?.clientCompanyId) {
            setClientCompanyState({ defaultId: data.clientCompanyId, showDropdown: false });
        } else if (!isAdmin) {
            setClientCompanyState({ defaultId: currentAppContext.companyId, showDropdown: false });
        } else {
            setClientCompanyState({ defaultId: undefined, showDropdown: true });
        }
    }, [data, currentAppContext.companyId, isAdmin]);

    if (clientCompanyState === undefined) {
        return null;
    }

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    {(objectTypeFieldId && (
                        <TopbarCrumbActive title={translate('common.editItem', translate('objectTypeField.objectTypeField'))} icon="fa-cogs" />
                    )) || <TopbarCrumbActive title={translate('common.addItem', translate('objectTypeField.objectTypeField'))} icon="fa-cogs" />}
                </TopbarLeft>
            </Topbar>
            <Content>
                <Tray>
                    <Panel small>
                        <PanelBody noPadding>
                            <ValidatingJsonSchemaForm
                                createSchema={getRequestBodySchema('/api/objectTypes/{objectTypeId}/objectTypeFields', 'post')}
                                updateSchema={getRequestBodySchema('/api/objectTypes/{objectTypeId}/objectTypeFields/{objectTypeFieldId}', 'post')}
                                createUrl={`/api/objectTypes/${objectTypeId}/objectTypeFields`}
                                updateUrl={`/api/objectTypes/${objectTypeId}/objectTypeFields/${objectTypeFieldId}`}
                                itemUrl={`/api/objectTypes/${objectTypeId}/objectTypeFields/${objectTypeFieldId}`}
                                id={objectTypeFieldId}
                                onSubmitted={handleSavedOrCancelled}
                                defaultItem={{
                                    dataType: ObjectTypeFieldDataType.String,
                                    unit: '',
                                    listOptions: [],
                                    category: '',
                                    clientCompanyId: clientCompanyState.defaultId,
                                }}
                            >
                                <EditPanel>
                                    {clientCompanyState.showDropdown && (
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <ValidatingFilterDropdownWithUrl
                                                    name="clientCompanyId"
                                                    topLabel={translate('common.client')}
                                                    placeholder={translate('common.select', translate('common.clients'))}
                                                    url={'/api/clients'}
                                                    disabled={isInEditMode}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                    )}
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormInputText name="name" topLabel={translate('common.name')} autoFocus />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormInputText name="unit" topLabel={translate('objectTypeField.unit')} />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormInputText name="category" topLabel={translate('objectTypeField.category')} />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingCulturedFormInputNumber name="order" topLabel={translate('common.order')} />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            {isInEditMode ? (
                                                <ValidatingFormEnumValue
                                                    name="dataType"
                                                    keyToTranslate="objectTypeFieldDataType"
                                                    topLabel={translate('objectTypeField.dataType')}
                                                />
                                            ) : (
                                                <ValidatingFilterDropdown
                                                    name="dataType"
                                                    topLabel={translate('objectTypeField.dataType')}
                                                    readonly
                                                    selectOptions={[
                                                        {
                                                            value: ObjectTypeFieldDataType.Boolean,
                                                            text: translate(`objectTypeFieldDataType.${ObjectTypeFieldDataType.Boolean}`),
                                                        },
                                                        {
                                                            value: ObjectTypeFieldDataType.List,
                                                            text: translate(`objectTypeFieldDataType.${ObjectTypeFieldDataType.List}`),
                                                        },
                                                        {
                                                            value: ObjectTypeFieldDataType.Number,
                                                            text: translate(`objectTypeFieldDataType.${ObjectTypeFieldDataType.Number}`),
                                                        },
                                                        {
                                                            value: ObjectTypeFieldDataType.String,
                                                            text: translate(`objectTypeFieldDataType.${ObjectTypeFieldDataType.String}`),
                                                        },
                                                    ]}
                                                />
                                            )}
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormDependentSection showIf={(item) => item.dataType === ObjectTypeFieldDataType.List}>
                                                <ValidatingFormInputTags name="listOptions" topLabel={translate('objectTypeField.listOptions')} />
                                            </ValidatingFormDependentSection>
                                        </EditPanelCell>
                                    </EditPanelRow>
                                </EditPanel>
                                <EditPanelButtons footer>
                                    <PanelButton text={translate('common.cancel')} onClick={handleSavedOrCancelled} />
                                    <ValidatingEditPanelSaveButton />
                                </EditPanelButtons>
                            </ValidatingJsonSchemaForm>
                        </PanelBody>
                    </Panel>
                </Tray>
            </Content>
        </>
    );
}
